/* Native About Us interactions. Deliberately scoped to .about-ritual. */
(() => {
  'use strict';

  const INSTANCE = '__scarletteAboutRitual';
  if (window[INSTANCE]) {
    window[INSTANCE].refresh();
    return;
  }

  const INTERACTIVE = 'a,button,input,select,textarea,label,summary,video,audio,[contenteditable="true"],[role="button"],[data-about-open]';
  const isAbout = (element) => element instanceof Element && Boolean(element.closest('.about-ritual'));
  const ownText = (element) => Array.from(element.childNodes).some((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
  const clamp = (number, lower, upper) => Math.min(upper, Math.max(lower, number));

  class AboutPage {
    constructor() {
      this.videos = new Map();
      this.dialogs = new Map();
      this.tracks = new Map();
      this.typeCache = new WeakMap();
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.abort = new AbortController();
      this.frame = 0;
      this.resizeTimer = 0;
      this.nav = null;
      this.navWrap = null;
      this.openDialog = null;
      this.lastOutsidePointer = null;
      this.initialHashHandled = false;
      this.anchorRequest = null;
      this.anchorTimer = 0;
      const signal = this.abort.signal;

      document.addEventListener('click', (event) => this.onClick(event), { signal });
      document.addEventListener('pointerdown', (event) => {
        this.cancelAnchor();
        this.onDialogPointerDown(event);
      }, { signal });
      ['wheel', 'touchstart'].forEach((name) => window.addEventListener(name, () => this.cancelAnchor(), { passive: true, signal }));
      document.addEventListener('keydown', (event) => {
        if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ', 'Spacebar'].includes(event.key)) this.cancelAnchor();
      }, { signal });
      window.addEventListener('scroll', () => {
        this.schedule();
        this.queueAnchorSettle(150);
      }, { passive: true, signal });
      document.addEventListener('scrollend', () => this.queueAnchorSettle(40), { signal });
      window.addEventListener('resize', () => {
        this.schedule();
        window.clearTimeout(this.resizeTimer);
        this.resizeTimer = window.setTimeout(() => this.measureType(), 120);
      }, { passive: true, signal });
      window.addEventListener('hashchange', () => this.followHash(), { signal });
      window.addEventListener('popstate', () => this.followHash(), { signal });
      document.addEventListener('visibilitychange', () => this.updateVideos(), { signal });
      ['stickyHide', 'stickyReveal', 'stickyReset'].forEach((name) => {
        document.addEventListener(name, () => {
          this.schedule();
          this.queueAnchorSettle(150);
        }, { signal });
      });
      document.addEventListener('shopify:section:load', () => this.refresh(), { signal });
      document.addEventListener('shopify:section:unload', (event) => this.unload(event.target), { signal });
      document.addEventListener('shopify:block:select', (event) => this.selectBlock(event), { signal });
      this.motion.addEventListener('change', () => this.updateVideos(), { signal });

      this.headerObserver = new MutationObserver(() => {
        this.schedule();
        this.queueAnchorSettle(150);
      });
      this.headerObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });
      this.resizeObserver = new ResizeObserver(() => this.schedule());
      this.videoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const state = this.videos.get(entry.target);
          if (!state) return;
          state.visible = entry.isIntersecting && entry.intersectionRatio > 0;
          this.updateVideo(state);
        });
      }, { threshold: [0, 0.05, 0.25] });
      this.refresh();
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
          this.measureType(true);
          this.schedule();
          this.followInitialHash();
        });
      } else {
        this.followInitialHash();
      }
    }

    refresh() {
      this.cleanDisconnected();
      const wrap = document.querySelector('.about-ritual [data-about-nav-wrap], .about-ritual[data-about-nav-wrap]');
      if (wrap !== this.navWrap) {
        if (this.nav) this.resizeObserver.unobserve(this.nav);
        this.navWrap = wrap;
        this.nav = wrap && wrap.querySelector('[data-about-nav]');
        if (this.nav) this.resizeObserver.observe(this.nav);
      }
      document.querySelectorAll('.about-ritual [data-about-video]').forEach((wrapper) => this.initVideo(wrapper));
      document.querySelectorAll('.about-ritual dialog[data-about-dialog]').forEach((dialog) => this.initDialog(dialog));
      document.querySelectorAll('.about-ritual [data-scroll-container],.about-ritual .enhanced-card-grid__items--scroll,.about-ritual .enhanced-card-grid__items--responsive').forEach((track) => this.initTrack(track));
      this.measureType(true);
      this.schedule();
    }

    cleanDisconnected() {
      for (const [wrapper, state] of this.videos) {
        if (wrapper.isConnected) continue;
        state.video.pause();
        state.abort.abort();
        this.videoObserver.unobserve(wrapper);
        this.videos.delete(wrapper);
      }
      for (const [dialog, state] of this.dialogs) {
        if (dialog.isConnected) continue;
        this.releaseDialog(dialog);
        state.abort.abort();
        this.dialogs.delete(dialog);
      }
      for (const [track, state] of this.tracks) {
        if (track.isConnected) continue;
        state.abort.abort();
        this.tracks.delete(track);
      }
    }

    unload(container) {
      if (this.anchorRequest && (container.contains(this.anchorRequest.target) || (this.nav && container.contains(this.nav)))) this.cancelAnchor();
      for (const [wrapper, state] of this.videos) {
        if (!container.contains(wrapper)) continue;
        state.video.pause();
        state.abort.abort();
        this.videoObserver.unobserve(wrapper);
        this.videos.delete(wrapper);
      }
      for (const [dialog, state] of this.dialogs) {
        if (!container.contains(dialog)) continue;
        if (dialog.open) dialog.close();
        this.releaseDialog(dialog);
        state.abort.abort();
        this.dialogs.delete(dialog);
      }
      for (const [track, state] of this.tracks) {
        if (!container.contains(track)) continue;
        state.abort.abort();
        this.tracks.delete(track);
      }
      if (this.nav && container.contains(this.nav)) {
        this.resizeObserver.unobserve(this.nav);
        this.nav = null;
        this.navWrap = null;
      }
      this.schedule();
    }

    schedule() {
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(() => {
        this.frame = 0;
        this.updateNav();
      });
    }

    headerOffset() {
      const value = getComputedStyle(document.documentElement).getPropertyValue('--sticky-group-height').trim();
      if (value && Number.isFinite(parseFloat(value))) return Math.max(0, parseFloat(value));
      const header = document.querySelector('.section-header:not(.shopify-section-header-hidden)');
      return header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
    }

    setRootVariable(name, value) {
      const root = document.documentElement;
      if (root.style.getPropertyValue(name) !== value) root.style.setProperty(name, value);
    }

    updateNav() {
      const offset = this.headerOffset();
      this.setRootVariable('--about-header-offset', `${offset}px`);
      if (!this.nav || !this.navWrap || !this.nav.isConnected) return;
      const height = this.nav.getBoundingClientRect().height;
      this.setRootVariable('--about-nav-height', `${height}px`);
      const fixed = this.navWrap.getBoundingClientRect().top <= offset;
      this.navWrap.classList.toggle('is-fixed', fixed);
      this.nav.classList.toggle('is-fixed', fixed);
      // The wrapper stays in normal flow while its navigation is fixed.
      if (fixed) this.navWrap.style.height = `${height}px`;
      else this.navWrap.style.removeProperty('height');
      const result = document.getElementById('results');
      const active = result && result.getBoundingClientRect().top <= offset + height + 24 ? '#results' : '#our-story';
      this.nav.querySelectorAll('a[href="#our-story"],a[href="#results"]').forEach((link) => {
        const selected = link.getAttribute('href') === active;
        link.classList.toggle('is-active', selected);
        if (selected) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    }

    scrollToAnchor(hash, behavior = 'smooth') {
      if (hash !== '#our-story' && hash !== '#results') return;
      const target = document.getElementById(hash.slice(1));
      if (!target || !isAbout(target)) return;
      this.cancelAnchor();
      this.updateNav();
      this.anchorRequest = { target, started: Date.now(), corrections: 0, stable: 0 };
      const top = this.anchorTop(target);
      if (this.motion.matches || behavior === 'auto') this.jumpTo(top);
      else window.scrollTo({ top, behavior });
      this.queueAnchorSettle(150);
      this.schedule();
    }

    anchorTop(target) {
      const navHeight = this.nav ? this.nav.getBoundingClientRect().height : 0;
      const top = window.scrollY + target.getBoundingClientRect().top - this.headerOffset() - navHeight - 16;
      const maximum = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      return clamp(top, 0, maximum);
    }

    jumpTo(top) {
      // An inherited theme-level smooth-scroll rule must not animate corrections
      // or reduced-motion navigation.
      const root = document.scrollingElement || document.documentElement;
      const previous = root.style.getPropertyValue('scroll-behavior');
      const priority = root.style.getPropertyPriority('scroll-behavior');
      root.style.setProperty('scroll-behavior', 'auto', 'important');
      window.scrollTo({ top, behavior: 'auto' });
      if (previous) root.style.setProperty('scroll-behavior', previous, priority);
      else root.style.removeProperty('scroll-behavior');
    }

    cancelAnchor() {
      window.clearTimeout(this.anchorTimer);
      this.anchorTimer = 0;
      this.anchorRequest = null;
    }

    queueAnchorSettle(delay) {
      if (!this.anchorRequest) return;
      window.clearTimeout(this.anchorTimer);
      this.anchorTimer = window.setTimeout(() => this.settleAnchor(), delay);
    }

    settleAnchor() {
      const request = this.anchorRequest;
      if (!request) return;
      if (!request.target.isConnected || !this.nav || Date.now() - request.started > 5000) {
        this.cancelAnchor();
        return;
      }
      this.updateNav();
      const top = this.anchorTop(request.target);
      if (Math.abs(window.scrollY - top) <= 2) {
        request.stable += 1;
        if (request.stable >= 2) this.cancelAnchor();
        else this.queueAnchorSettle(80);
        return;
      }
      if (request.corrections >= 4) {
        this.cancelAnchor();
        return;
      }
      request.stable = 0;
      request.corrections += 1;
      // Shrine can hide or reveal its header during the original smooth scroll.
      // Correct only after scrolling rests, without starting another animation.
      this.jumpTo(top);
      this.schedule();
      this.queueAnchorSettle(150);
    }

    followHash() {
      if (location.hash === '#our-story' || location.hash === '#results') this.scrollToAnchor(location.hash, 'auto');
    }

    followInitialHash() {
      if (this.initialHashHandled) return;
      this.initialHashHandled = true;
      window.requestAnimationFrame(() => window.requestAnimationFrame(() => this.followHash()));
    }

    onClick(event) {
      if (!(event.target instanceof Element) || !isAbout(event.target)) return;
      const anchor = event.target.closest('[data-about-nav] a');
      if (anchor && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
        const hash = anchor.getAttribute('href');
        if (hash === '#our-story' || hash === '#results') {
          event.preventDefault();
          if (location.hash !== hash) history.pushState(null, '', hash);
          this.scrollToAnchor(hash);
          return;
        }
      }
      const open = event.target.closest('[data-about-open]');
      if (open) {
        const dialog = document.getElementById(open.dataset.aboutOpen);
        if (dialog && dialog.matches('dialog[data-about-dialog]') && isAbout(dialog)) {
          event.preventDefault();
          this.showDialog(dialog, open);
        }
        return;
      }
      const close = event.target.closest('[data-about-close]');
      if (close) {
        const dialog = close.closest('dialog[data-about-dialog]');
        if (dialog) dialog.close();
        return;
      }
      const play = event.target.closest('[data-about-play]');
      const mute = event.target.closest('[data-about-mute]');
      if (play || mute) {
        const wrapper = (play || mute).closest('[data-about-video]');
        const state = wrapper && this.videos.get(wrapper);
        if (!state) return;
        if (play) {
          if (state.video.paused) {
            state.manualPaused = false;
            state.userAllowed = true;
            this.playVideo(state);
          } else {
            state.manualPaused = true;
            state.video.pause();
          }
        } else state.video.muted = !state.video.muted;
        this.syncVideoControls(state);
        return;
      }
      const dialog = event.target.closest('dialog[data-about-dialog]');
      if (dialog && dialog.open && this.lastOutsidePointer === dialog && this.outsideDialog(dialog, event)) dialog.close();
      this.lastOutsidePointer = null;
    }

    initVideo(wrapper) {
      if (this.videos.has(wrapper)) return;
      const video = wrapper.matches('video') ? wrapper : wrapper.querySelector('video');
      if (!video) return;
      const abort = new AbortController();
      const state = { wrapper, video, abort, visible: false, manualPaused: false, userAllowed: false, pending: false };
      this.videos.set(wrapper, state);
      video.removeAttribute('autoplay');
      video.playsInline = true;
      const updatePoster = () => {
        const poster = window.innerWidth < 768 && video.dataset.aboutPosterMobile ? video.dataset.aboutPosterMobile : video.dataset.aboutPosterDesktop;
        if (poster && video.getAttribute('poster') !== poster) video.poster = poster;
      };
      updatePoster();
      window.addEventListener('resize', updatePoster, { passive: true, signal: abort.signal });
      if (video.hasAttribute('muted')) video.muted = true;
      ['play', 'pause', 'ended', 'volumechange'].forEach((name) => {
        video.addEventListener(name, () => this.syncVideoControls(state), { signal: abort.signal });
      });
      this.syncVideoControls(state);
      this.videoObserver.observe(wrapper);
    }

    playVideo(state) {
      if (state.pending) return;
      state.pending = true;
      state.video.preload = 'metadata';
      const promise = state.video.play();
      Promise.resolve(promise).catch(() => {
        // Autoplay can be unavailable; the visible play button remains usable.
      }).finally(() => {
        state.pending = false;
        if (!state.visible || document.hidden || state.manualPaused) state.video.pause();
        this.syncVideoControls(state);
      });
    }

    updateVideo(state) {
      const allowed = !this.motion.matches || state.userAllowed;
      if (state.visible && !document.hidden && !state.manualPaused && allowed) this.playVideo(state);
      else state.video.pause();
    }

    updateVideos() {
      this.videos.forEach((state) => this.updateVideo(state));
    }

    syncVideoControls(state) {
      const playing = !state.video.paused && !state.video.ended;
      state.wrapper.classList.toggle('is-playing', playing);
      state.wrapper.classList.toggle('is-muted', state.video.muted);
      state.wrapper.querySelectorAll('[data-about-play]').forEach((button) => {
        button.classList.toggle('is-playing', playing);
        button.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
        button.setAttribute('aria-pressed', String(playing));
      });
      state.wrapper.querySelectorAll('[data-about-mute]').forEach((button) => {
        button.classList.toggle('is-muted', state.video.muted);
        button.setAttribute('aria-label', state.video.muted ? 'Unmute video' : 'Mute video');
        button.setAttribute('aria-pressed', String(state.video.muted));
      });
    }

    initDialog(dialog) {
      if (this.dialogs.has(dialog)) return;
      const abort = new AbortController();
      this.dialogs.set(dialog, { abort, opener: null });
      dialog.addEventListener('close', () => this.releaseDialog(dialog), { signal: abort.signal });
      dialog.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;
        const controls = Array.from(dialog.querySelectorAll('a[href],button,input,select,textarea,summary,[tabindex],audio[controls],video[controls]')).filter((element) => !element.disabled && element.tabIndex >= 0 && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (!first) { event.preventDefault(); dialog.focus(); return; }
        if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog)) { event.preventDefault(); first.focus(); }
      }, { signal: abort.signal });
      // Escape retains native dialog behavior; Tab cycles inside the drawer.
    }

    showDialog(dialog, opener) {
      this.initDialog(dialog);
      if (dialog.open) return;
      if (this.openDialog && this.openDialog !== dialog) this.openDialog.close();
      const state = this.dialogs.get(dialog);
      state.opener = opener || document.activeElement;
      dialog.showModal();
      this.openDialog = dialog;
      document.body.classList.add('about-ritual-dialog-open');
      this.setDialogTriggers(dialog, true);
      const close = dialog.querySelector('[data-about-close]');
      if (close) close.focus({ preventScroll: true });
    }

    setDialogTriggers(dialog, expanded) {
      document.querySelectorAll('.about-ritual [data-about-open]').forEach((trigger) => {
        if (trigger.dataset.aboutOpen === dialog.id) trigger.setAttribute('aria-expanded', String(expanded));
      });
    }

    releaseDialog(dialog) {
      const state = this.dialogs.get(dialog);
      if (this.openDialog === dialog) this.openDialog = null;
      const anyOpen = Array.from(this.dialogs.keys()).some((item) => item.open && item.isConnected);
      if (!anyOpen) document.body.classList.remove('about-ritual-dialog-open');
      this.setDialogTriggers(dialog, false);
      this.videos.forEach((videoState) => {
        if (dialog.contains(videoState.wrapper)) videoState.video.pause();
      });
      if (!this.openDialog && state && state.opener && state.opener.isConnected) state.opener.focus({ preventScroll: true });
      if (state) state.opener = null;
      this.lastOutsidePointer = null;
    }

    outsideDialog(dialog, event) {
      const panel = dialog.querySelector('[data-about-dialog-panel]') || dialog;
      if (panel !== dialog && panel.contains(event.target)) return false;
      if (panel === dialog && event.target !== dialog) return false;
      const rect = panel.getBoundingClientRect();
      return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
    }

    onDialogPointerDown(event) {
      if (!(event.target instanceof Element)) return;
      const dialog = event.target.closest('dialog[data-about-dialog]');
      this.lastOutsidePointer = dialog && dialog.open && this.outsideDialog(dialog, event) ? dialog : null;
    }

    initTrack(track) {
      if (this.tracks.has(track)) return;
      const abort = new AbortController();
      const signal = abort.signal;
      const state = { abort, down: false, dragging: false, suppressClick: false, x: 0, scroll: 0, pointer: null };
      this.tracks.set(track, state);
      if (!track.hasAttribute('data-scroll-container')) track.setAttribute('data-scroll-container', '');
      if (!track.hasAttribute('tabindex')) track.tabIndex = 0;
      if (!track.hasAttribute('role')) track.setAttribute('role', 'region');
      if (!track.hasAttribute('aria-roledescription')) track.setAttribute('aria-roledescription', 'carousel');
      if (!track.hasAttribute('aria-label') && !track.hasAttribute('aria-labelledby')) {
        const heading = track.closest('.about-ritual').querySelector('h2,h3,h4');
        track.setAttribute('aria-label', heading ? heading.textContent.trim() : 'Scrollable cards');
      }
      track.addEventListener('keydown', (event) => {
        if (event.target !== track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const max = Math.max(0, track.scrollWidth - track.clientWidth);
        const left = event.key === 'Home' ? 0 : event.key === 'End' ? max : track.scrollLeft + (event.key === 'ArrowLeft' ? -1 : 1) * track.clientWidth * 0.8;
        track.scrollTo({ left: clamp(left, 0, max), behavior: this.motion.matches ? 'auto' : 'smooth' });
      }, { signal });
      track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest(INTERACTIVE)) return;
        state.down = true;
        state.dragging = false;
        state.suppressClick = false;
        state.x = event.clientX;
        state.scroll = track.scrollLeft;
        state.pointer = event.pointerId;
      }, { signal });
      track.addEventListener('pointermove', (event) => {
        if (!state.down || event.pointerId !== state.pointer) return;
        const distance = event.clientX - state.x;
        if (!state.dragging && Math.abs(distance) < 5) return;
        if (!state.dragging) {
          state.dragging = true;
          track.classList.add('is-dragging');
          track.setPointerCapture(event.pointerId);
        }
        event.preventDefault();
        track.scrollLeft = state.scroll - distance;
      }, { signal });
      const finish = (event) => {
        if (event.pointerId !== state.pointer) return;
        state.suppressClick = state.dragging;
        state.down = false;
        state.dragging = false;
        track.classList.remove('is-dragging');
        if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
        state.pointer = null;
      };
      track.addEventListener('pointerup', finish, { signal });
      track.addEventListener('pointercancel', finish, { signal });
      track.addEventListener('pointerleave', (event) => {
        if (!track.hasPointerCapture(event.pointerId)) finish(event);
      }, { signal });
      track.addEventListener('click', (event) => {
        if (!state.suppressClick) return;
        state.suppressClick = false;
        event.preventDefault();
        event.stopPropagation();
      }, { capture: true, signal });
      // Touch scrolling stays native, including vertical page gestures.
    }

    measureType(force = false) {
      if (force) this.typeCache = new WeakMap();
      const width = Math.round(window.innerWidth);
      const targets = new Set();
      document.querySelectorAll('.about-ritual [data-about-type],.about-ritual[data-about-type]').forEach((wrapper) => {
        [wrapper, ...wrapper.querySelectorAll('*')].forEach((element) => {
          if (!ownText(element) || element.closest('svg,script,style,template,noscript')) return;
          targets.add(element);
        });
      });
      // Remove every prior scale before reading any computed size. This avoids
      // measuring a child after its parent has already been enlarged.
      targets.forEach((element) => {
        element.removeAttribute('data-about-scaled');
        element.style.removeProperty('--about-base-font-size');
      });
      const sizes = [];
      targets.forEach((element) => {
        let cache = this.typeCache.get(element);
        if (!cache) {
          cache = new Map();
          this.typeCache.set(element, cache);
        }
        let size = cache.get(width);
        if (!size) {
          size = parseFloat(getComputedStyle(element).fontSize);
          if (Number.isFinite(size) && size > 0) cache.set(width, size);
        }
        if (Number.isFinite(size) && size > 0) sizes.push([element, size]);
      });
      sizes.forEach(([element, size]) => {
        element.style.setProperty('--about-base-font-size', `${size}px`);
        element.setAttribute('data-about-scaled', '');
      });
      this.schedule();
    }

    selectBlock(event) {
      const block = event.target;
      if (!(block instanceof Element) || !isAbout(block)) return;
      const dialog = block.closest('dialog[data-about-dialog]');
      if (dialog) {
        const opener = Array.from(document.querySelectorAll('.about-ritual [data-about-open]')).find((item) => item.dataset.aboutOpen === dialog.id);
        this.showDialog(dialog, opener);
        return;
      }
      block.scrollIntoView({ block: 'nearest', inline: 'center', behavior: this.motion.matches ? 'auto' : 'smooth' });
      const trigger = block.matches('[data-about-open]') ? block : block.querySelector('[data-about-open]');
      if (trigger) {
        const target = document.getElementById(trigger.dataset.aboutOpen);
        if (target && target.matches('dialog[data-about-dialog]')) this.showDialog(target, trigger);
      }
    }
  }

  const boot = () => {
    if (!document.querySelector('.about-ritual')) return;
    window[INSTANCE] = new AboutPage();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
